import { Controller } from "@hotwired/stimulus"

// Copies text from a `data-copy-button-target="source"` input/textarea
// or a `data-value` attribute on the action element. Flips the
// `data-copy-button-target="label"` text to "Copied!" for ~1.5s.
//
// Usage:
//   <div data-controller="copy-button">
//     <input data-copy-button-target="source" value="…" readonly>
//     <button data-action="click->copy-button#copy">
//       <span data-copy-button-target="label">Copy</span>
//     </button>
//   </div>
//
// Falls back to a hidden-textarea + execCommand("copy") path on
// non-secure contexts where `navigator.clipboard` is unavailable.
export default class extends Controller {
  static targets = ["source", "label"]
  static values = { copiedText: { type: String, default: "Copied!" } }

  copy(event) {
    event?.preventDefault?.()
    const text = this._sourceText(event?.currentTarget)
    if (!text) return

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => this._flashLabel())
        .catch(() => this._fallbackCopy(text))
    } else {
      this._fallbackCopy(text)
    }
  }

  _sourceText(actionEl) {
    const explicit = actionEl?.getAttribute?.("data-value")
    if (explicit) return explicit
    if (this.hasSourceTarget) {
      return this.sourceTarget.value ?? this.sourceTarget.textContent ?? ""
    }
    return ""
  }

  _fallbackCopy(text) {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "absolute"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.select()
    try { document.execCommand("copy") } catch (_) { /* no-op */ }
    document.body.removeChild(ta)
    this._flashLabel()
  }

  _flashLabel() {
    if (!this.hasLabelTarget) return
    const original = this.labelTarget.textContent
    this.labelTarget.textContent = this.copiedTextValue
    clearTimeout(this._labelTimer)
    this._labelTimer = setTimeout(() => {
      this.labelTarget.textContent = original
    }, 1500)
  }
}
